import {Component, EventEmitter, Input, Output} from '@angular/core';
import {FactorKind, RateConfig, RateFactor} from "../../../../model/rate/RateConfig";
import {LanguagePipe} from "../../../pipe/language.pipe";
import {ValidationResult} from "../../../../model/rate/RateConfigValidator";
import {EMPTY_RESULT, subtreeIssues} from "../validation-paths";

/**
 * One collapsible section per {@link FactorKind}. Lists the factors of
 * that kind for the current scope, exposes "Add factor" affordance, and
 * forwards per-factor change/delete events upward keyed by the
 * per-kind index (the parent scope panel translates that into the
 * absolute index in `config.factors[]`).
 *
 * Per the human's direction (May 6, 2026), factors are presented
 * grouped by Kind in {@link FACTOR_KIND_ORDER} order. The user does
 * not see a `kind` selector inside individual factors — kind is
 * implicit from which section the "Add factor" was clicked in, and
 * since the auto-id includes kind, a factor cannot accidentally drift
 * between kinds.
 */
@Component({
  selector: 'app-rate-kind-section',
  templateUrl: './kind-section.component.html',
  styleUrls: ['./kind-section.component.css']
})
export class KindSectionComponent {
  @Input() kind: FactorKind;
  @Input() factors: RateFactor[] = [];
  @Input() scopeConfig: Partial<RateConfig> = {};
  @Input() readOnly: boolean = false;
  /** Validator path prefix of the owning scope (e.g. `/SHORT_RUN`). */
  @Input() scopePath: string = '';
  /** Absolute index in `scopeConfig.factors[]` for each factor in {@link factors}. */
  @Input() factorAbsoluteIndexes: number[] = [];
  @Input() validation: ValidationResult = EMPTY_RESULT;

  @Output() addFactor = new EventEmitter<void>();
  @Output() factorChange = new EventEmitter<{ index: number; factor: RateFactor }>();
  @Output() factorDelete = new EventEmitter<number>();

  readonly localizeNumber = (n: any) => LanguagePipe.localizeNumber(n);

  /** Validator path prefix for the i-th factor in this kind list. */
  factorPath(perKindIndex: number): string {
    const abs = this.factorAbsoluteIndexes[perKindIndex];
    return abs == null ? '' : `${this.scopePath}/factors/${abs}`;
  }

  /** Total error count across all factors in this section (for the header badge). */
  get sectionErrorCount(): number {
    if (!this.validation || this.validation.valid) { return 0; }
    let n = 0;
    for (let i = 0; i < this.factors.length; i++) {
      n += subtreeIssues(this.validation, this.factorPath(i)).errors.length;
    }
    return n;
  }

  /** Human-readable label for the kind, used in the section header. */
  kindLabel(): string {
    switch (this.kind) {
      case 'PER_ORDER':            return LanguagePipe.get('Per order', 'compressSpaces');
      case 'PER_ITEM':             return LanguagePipe.get('Per item', 'compressSpaces');
      case 'PER_QUANTITY':         return LanguagePipe.get('Per book', 'compressSpaces');
      case 'PER_NUMERIC_PROPERTY': return LanguagePipe.get('Per page', 'compressSpaces');
      case 'PER_PACKAGE':          return LanguagePipe.get('Per package', 'compressSpaces');
      default:                     return this.kind;
    }
  }

  /** Tooltip explaining what this kind contributes. */
  kindDescription(): string {
    switch (this.kind) {
      case 'PER_ORDER':
        return LanguagePipe.get('Adds once per order, regardless of quantity.', 'compressSpaces');
      case 'PER_ITEM':
        return LanguagePipe.get('Adds once per item, independent of quantity.', 'compressSpaces');
      case 'PER_QUANTITY':
        return LanguagePipe.get('Adds per book in the order (amount times quantity).', 'compressSpaces');
      case 'PER_NUMERIC_PROPERTY':
        return LanguagePipe.get('Adds amount times a numeric property (e.g. pages) per book.', 'compressSpaces');
      case 'PER_PACKAGE':
        return LanguagePipe.get('Adds amount once per package in the order. Iterates the order\'s packageTypes[] and resolves each element against this factor\'s groups.', 'compressSpaces');
      default:
        return '';
    }
  }

  onChildChange(index: number, updated: RateFactor): void {
    this.factorChange.emit({index, factor: updated});
  }

  onChildDelete(index: number): void {
    this.factorDelete.emit(index);
  }
}

